import React, { useState, useEffect } from 'react';
import { verifyAdmin, sendCredentialsEmail } from '../../services/api';
import Alert from '../common/Alert';
import '../common/RegistrationForm.css';

const AdminCredentials = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authForm, setAuthForm] = useState({ email: '', password: '' });
  const [authError, setAuthError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    enrollmentId: '',
    personalEmail: '',
    loginEmail: '',
    password: ''
  });
  const [alert, setAlert] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Check session storage for existing auth
    const storedAuth = sessionStorage.getItem('adminAuth');
    if (storedAuth) {
      try {
        const parsed = JSON.parse(storedAuth);
        if (parsed.email && parsed.password) {
          setAuthForm(parsed);
          setIsAuthenticated(true);
        }
      } catch (e) {
        sessionStorage.removeItem('adminAuth');
      }
    }
  }, []);

  const handleAuthChange = (e) => {
    const { name, value } = e.target;
    setAuthForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await verifyAdmin(authForm);
      if (response.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuth', JSON.stringify(authForm));
      } else {
        setAuthError(response.message || 'Invalid credentials');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to authenticate';
      setAuthError(errorMsg);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlert(null);

    try {
      // Send both auth and form data
      const payload = {
        adminEmail: authForm.email,
        adminPassword: authForm.password,
        ...formData
      };

      const response = await sendCredentialsEmail(payload);

      if (response.success) {
        setAlert({ type: 'success', message: response.message });
        setFormData({
          name: '',
          enrollmentId: '',
          personalEmail: '',
          loginEmail: '',
          password: ''
        });
      } else {
        setAlert({ type: 'error', message: response.message || 'Failed to send email' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'An error occurred while sending the email.';

      // If unauthorized, clear session and force re-login
      if (error.response?.status === 401) {
        sessionStorage.removeItem('adminAuth');
        setIsAuthenticated(false);
        setAuthError('Session expired or invalid credentials. Please log in again.');
      } else {
        setAlert({ type: 'error', message: errorMsg });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="registration-container">
        <div className="registration-form-wrapper" style={{ maxWidth: '400px', margin: '0 auto' }}>
          <img src="/assets/BigLogo.png" alt="Educatory Logo" className="educatory-logo" />
          <h2 style={{ textAlign: 'center' }}>Admin Access</h2>
          <p className="subtitle" style={{ textAlign: 'center' }}>Please verify your credentials to continue</p>

          {authError && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center', fontSize: '13px' }}>{authError}</div>}

          <form className="registration-form" onSubmit={handleAuthSubmit}>
            <div className="form-group" style={{ margin: '0 0 1rem' }}>
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={authForm.email}
                onChange={handleAuthChange}
                required
              />
            </div>

            <div className="form-group" style={{ margin: '0 0 1rem' }}>
              <label>Password</label>
              <input
                type="password"
                name="password"
                value={authForm.password}
                onChange={handleAuthChange}
                required
              />
            </div>

            <button type="submit" className="submit-button" style={{ width: '100%' }}>
              Verify
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-container">
      <div className="registration-form-wrapper">
        <img src="/assets/BigLogo.png" alt="Educatory Logo" className="educatory-logo" />
        <h2>Send Dashboard Credentials</h2>
        <p className="subtitle">Fill out the form to send login credentials to the student.</p>

        {alert && <Alert type={alert.type} message={alert.message} />}

        <form className="registration-form" onSubmit={handleFormSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Student Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder="e.g. Kartik"
                required
              />
            </div>

            <div className="form-group">
              <label>Enrollment ID</label>
              <input
                type="text"
                name="enrollmentId"
                value={formData.enrollmentId}
                onChange={handleFormChange}
                placeholder="e.g. 20260721ed001"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Personal Email</label>
              <input
                type="email"
                name="personalEmail"
                value={formData.personalEmail}
                onChange={handleFormChange}
                placeholder="e.g. student@gmail.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Login Email</label>
              <input
                type="email"
                name="loginEmail"
                value={formData.loginEmail}
                onChange={handleFormChange}
                placeholder="e.g. 20260721ed001@educatory.ac"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Password</label>
              <input
                type="text"
                name="password"
                value={formData.password}
                onChange={handleFormChange}
                placeholder="e.g. Pass123!"
                required
              />
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Sending Email...' : 'Send Credentials Email'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminCredentials;
